/**
 * pendingCheckout — carries a `?pack=<id>` purchase intent across the auth
 * flow (B-258).
 *
 * Marketing links like /try?pack=pack_10 land on /signup (or /login). Email
 * signup then detours through "check your email" → the verification link →
 * sign-in, often in a NEW tab, so a query param cannot survive the round
 * trip. A localStorage stash (same browser) can. The dashboard consumes the
 * stash after auth and goes straight to Stripe checkout.
 */

const KEY = 'at_pending_checkout';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — a stale intent shouldn't ambush the user

/** Stores the pack id the visitor intended to buy. */
export function stashPack(packId: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ packId, ts: Date.now() }));
  } catch {
    // Private mode / storage denied: the user just buys via the normal modal.
  }
}

/** Returns the stashed pack id, or null when absent/expired (expired clears). */
export function readPack(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { packId, ts } = JSON.parse(raw) as { packId?: string; ts?: number };
    if (!packId || !ts || Date.now() - ts > MAX_AGE_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return packId;
  } catch {
    return null;
  }
}

/** Clears the stash (call BEFORE redirecting to Stripe so cancel can't loop). */
export function clearPack(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
